import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import {
	Button,
	Empty,
	Form,
	FormInstance,
	Input,
	InputNumber,
	Select,
	Slider,
	Switch,
	Tooltip,
} from 'antd';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faTrash } from '@fortawesome/pro-duotone-svg-icons';

import * as CmsImageService from '../../../../services/bb-api/cms/image';

import { AnimationCollapse } from './animation';
import { txt } from '../../../../locales/i18n';
import { ImageCatalogue } from '../image-catalogue';
import { getImageSource } from '../layouts/utils';

export type BaseContainerPanelProps = {
	propertyKey: string;
	formRef: React.RefObject<FormInstance>;
	onSectionChange: (changedValues: any, values: any) => void;
};

export const BaseContainerPanel = (props: BaseContainerPanelProps) => {
	const [openImageCatalogue, setOpenImageCatalogue] = useState(false);
	const [imageName, setImageName] = useState<string | null>(null);

	useEffect(() => {
		const imageNameInput = document.querySelector<HTMLInputElement>('#container_coverSource');
		if (imageNameInput === null) return;
		setImageName(imageNameInput.value || null);
	}, []);

	const handleSelectImage = (imageName: string) => {
		const form = props.formRef.current;
		if (form === null) return;

		form.setFields([{ name: [props.propertyKey, 'coverSource'], value: imageName }]);
		const values = form.getFieldsValue();
		if (values) props.onSectionChange({}, values);

		setImageName(imageName);
	};

	const handleRemoveImage = () => {
		const form = props.formRef.current;
		if (form === null) return;

		form.setFields([{ name: [props.propertyKey, 'coverSource'], value: '' }]);
		const values = form.getFieldsValue();
		if (values) props.onSectionChange({}, values);

		setImageName(null);
	};

	return (
		<BaseContainerPanelStyled>
			<div className="row-container">
				<div>
					<label>{txt('cms.full screen')}</label>
					<Form.Item name={[props.propertyKey, 'fullscreen']} valuePropName="checked">
						<Switch />
					</Form.Item>
				</div>

				<div>
					<label>{txt('cms.color')}</label>
					<Form.Item name={[props.propertyKey, 'backgroundColor']} noStyle>
						<Input type="color" />
					</Form.Item>
				</div>
			</div>

			<label>{txt('cms.cover source')}</label>
			<div className="image-source-container">
				<Form.Item name={[props.propertyKey, 'coverSource']} noStyle>
					<Input disabled />
				</Form.Item>

				<Tooltip title={txt('global.delete')}>
					<Button danger type="primary" onClick={handleRemoveImage}>
						<FontAwesomeIcon icon={faTrash} />
					</Button>
				</Tooltip>
			</div>

			<div className="image-preview-container" onClick={() => setOpenImageCatalogue(true)}>
				{imageName && <img alt="preview" src={getImageSource(imageName)} />}
				{imageName === null && <Empty />}
			</div>

			<div className="row-container">
				<div>
					<label>{txt('cms.cover fit')}</label>
					<Form.Item name={[props.propertyKey, 'coverFit']} initialValue="cover" noStyle>
						<Select>
							<Select.Option value="cover">Cover</Select.Option>
							<Select.Option value="contain">Contain</Select.Option>
						</Select>
					</Form.Item>
				</div>

				<div>
					<label>
						{txt('cms.cover position')}{' '}
						<a
							target="_blank"
							rel="noreferrer"
							href="https://developer.mozilla.org/en-US/docs/Web/CSS/object-position"
						>
							<FontAwesomeIcon icon={faCircleInfo} />
						</a>
					</label>
					<Form.Item
						initialValue="center"
						name={[props.propertyKey, 'coverPosition']}
						noStyle
					>
						<Input />
					</Form.Item>
				</div>
			</div>

			<AnimationCollapse
				propertyKey={props.propertyKey}
				formRef={props.formRef}
				onSectionChange={props.onSectionChange}
			/>

			<ImageCatalogue
				useVisible={[openImageCatalogue, setOpenImageCatalogue]}
				sourcePrefix="https://filer.boblberg.dk/cms/large_"
				onSelectImage={handleSelectImage}
				onQuery={CmsImageService.query}
				onCreate={CmsImageService.create}
				onRemove={CmsImageService.remove}
				showTransparent
			/>
		</BaseContainerPanelStyled>
	);
};

const BaseContainerPanelStyled = styled.div`
	.row-container {
		display: flex;
		gap: 2ch;
	}

	.form-item-container {
		flex: 1;
	}

	.image-source-container {
		display: flex;
		gap: 0.65rem;
		padding-bottom: 0.65rem;
	}

	.image-preview-container {
		width: 100%;
		aspect-ratio: 2;
		background-color: #eee;
		border-radius: 1rem;
		overflow: hidden;
		display: flex;
		justify-content: center;
		cursor: pointer;
		border: 1px solid #eee;
		margin-bottom: 0.65rem;

		&:hover {
			border: 2px solid dodgerblue;
		}

		img {
			object-fit: contain;
		}

		.ant-empty {
			align-self: center;
		}
	}
`;

export type ContainerPanelProps = {
	propertyKey: string;
	formRef: React.RefObject<FormInstance>;
	onSectionChange: (changedValues: any, values: any) => void;
};

export const ContainerPanel = (props: ContainerPanelProps) => {
	const borderSwitchRef = useRef<HTMLElement>(null);
	const [showBorder, setShowBorder] = useState(false);

	useEffect(() => {
		const borderSwitch = borderSwitchRef.current;
		if (borderSwitch === null) return;

		const showBorder = borderSwitch.getAttribute('aria-checked') === 'true';
		setShowBorder(showBorder);
	}, []);

	return (
		<ContainerPanelStyled>
			<div className="row-container">
				<div>
					<label>{txt('cms.color')}</label>
					<Form.Item name={[props.propertyKey, 'backgroundColor']} noStyle>
						<Input type="color" />
					</Form.Item>
				</div>

				<div className="form-item-container">
					<label>{txt('cms.opacity')}</label>

					<div style={{ display: 'flex' }}>
						<Form.Item name={[props.propertyKey, 'backgroundOpacity']} noStyle>
							<Slider min={0} max={255} style={{ flex: 1 }} />
						</Form.Item>

						<Form.Item name={[props.propertyKey, 'backgroundOpacity']}>
							<InputNumber
								bordered={false}
								size="small"
								min={0}
								max={255}
								style={{ width: '6ch' }}
							/>
						</Form.Item>
					</div>
				</div>
			</div>

			<div className="form-item-container">
				<label>{txt('cms.blur')}</label>
				<div style={{ display: 'flex' }}>
					<Form.Item name={[props.propertyKey, 'backgroundBlur']} noStyle>
						<Slider min={0} max={50} style={{ flex: 1 }} />
					</Form.Item>

					<Form.Item name={[props.propertyKey, 'backgroundBlur']}>
						<InputNumber
							bordered={false}
							size="small"
							min={0}
							max={50}
							style={{ width: '6ch' }}
						/>
					</Form.Item>
				</div>
			</div>

			<div className="row-container">
				<div className="form-item-container">
					<label>{txt('cms.border')}</label>
					<Form.Item name={[props.propertyKey, 'showBorder']} valuePropName="checked">
						<Switch ref={borderSwitchRef} onChange={setShowBorder} />
					</Form.Item>
				</div>

				<div className="form-item-container">
					<label>{txt('cms.border color')}</label>
					<Form.Item name={[props.propertyKey, 'borderColor']}>
						<Input type="color" disabled={showBorder === false} />
					</Form.Item>
				</div>
			</div>

			<div className="row-container">
				<div className="form-item-container">
					<label>{txt('cms.border width')}</label>

					<div style={{ display: 'flex' }}>
						<Form.Item name={[props.propertyKey, 'borderWidth']} noStyle>
							<Slider
								min={0}
								max={20}
								style={{ flex: 1 }}
								disabled={showBorder === false}
							/>
						</Form.Item>

						<Form.Item name={[props.propertyKey, 'borderWidth']}>
							<InputNumber
								bordered={false}
								size="small"
								min={0}
								max={20}
								style={{ width: '6ch' }}
								disabled={showBorder === false}
							/>
						</Form.Item>
					</div>
				</div>

				<div className="form-item-container">
					<label>{txt('cms.border radius')}</label>

					<div style={{ display: 'flex' }}>
						<Form.Item name={[props.propertyKey, 'borderRadius']} noStyle>
							<Slider min={0} max={100} style={{ flex: 1 }} />
						</Form.Item>

						<Form.Item name={[props.propertyKey, 'borderRadius']}>
							<InputNumber
								bordered={false}
								size="small"
								min={0}
								max={100}
								style={{ width: '6ch' }}
							/>
						</Form.Item>
					</div>
				</div>
			</div>

			<AnimationCollapse
				propertyKey={props.propertyKey}
				formRef={props.formRef}
				onSectionChange={props.onSectionChange}
			/>
		</ContainerPanelStyled>
	);
};

const ContainerPanelStyled = styled.div`
	.row-container {
		display: flex;
		gap: 2ch;
	}

	.form-item-container {
		flex: 1;
	}
`;
